import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import Page from '../../../components/Page';
import MetaTags from '../../../components/MetaTags';
import Grid from '@material-ui/core/Grid';
import Container from '@material-ui/core/Container';
import { ProviderAPI } from '../../../api';
import { ProviderStore } from '../../../store';
import { connect } from 'react-redux';
import PageTitle from '../../../components/PageTitle';
import PageSubtitle from '../../../components/PageSubtitle';
import MainImage from '../components/MainImage';
import WidgetBar from '../components/WidgetBar';
import TabsPanel from '../components/TabsPanel';
import ProfileInfoPanel from '../components/ProfileInfoPanel';
import LocationPanel from '../../../components/location/LocationPanel';
import config from '../../../config';
import * as QueryString from 'query-string';
import { useHistory } from 'react-router-dom';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import AccreditationBadge from '../components/AccreditationBadge';
import ActionButtons from '../components/ActionButtons';
import ClaimProfile from '../components/ClaimProfile';
import * as gtm from '../../../lib/gtm';
import * as track from '../../../lib/track';
import LoadingSkeleton from '../components/LoadingSkeleton';
import UserFeaturesCTA from '../../../components/widgets/UserFeaturesCTA';

const desktopStyles = makeStyles(() => ({
  leftPane: {
    paddingRight: '3rem',
  }
}));

const DesktopLayout = ({providerUuid, data, subtitle, tab, handleTabChange}) => {
  const classes = desktopStyles();
  return (
    <Container maxWidth="lg" fixed disableGutters={true}>
      <Grid container spacing={6}>
        <Grid item md={8}>
          <div className={classes.leftPane}>
            <MainImage data={data} />
            <PageTitle text={data.name} />
            <PageSubtitle text={subtitle} />
            <WidgetBar data={data} />
            <TabsPanel
              data={data}
              itemType="provider"
              showReviews={true}
              showListings={true}
              selectedTab={tab}
              handleChange={handleTabChange}
            />
            <LocationPanel providerUuid={providerUuid} />
          </div>
        </Grid>
        <Grid item md={4}>
          <div>
            <UserFeaturesCTA />
            <ClaimProfile
              providerUuid={providerUuid}
              claimed={data.claimed}
            />
            <AccreditationBadge data={data}/>
            <ActionButtons
              providerUuid={providerUuid}
              itemType="provider"
              data={data}
            />
            <ProfileInfoPanel data={data} />
          </div>
        </Grid>
      </Grid>
    </Container>
  );
};

DesktopLayout.propTypes = {
  providerUuid: PropTypes.string.isRequired,
  data: PropTypes.object.isRequired,
  subtitle: PropTypes.string.isRequired,
  tab: PropTypes.number.isRequired,
  handleTabChange: PropTypes.func.isRequired,
};

const MobileLayout = ({providerUuid, data, subtitle, tab, handleTabChange}) => {
  return (
    <Container maxWidth="sm" disableGutters={true} style={{ overflowX: 'hidden' }}>
      <Grid container spacing={0}>
        <Grid item sm={12}>
          <div>
            <MainImage data={data} />
            <PageTitle text={data.name} />
            <PageSubtitle text={subtitle} />
            <UserFeaturesCTA />
            <ClaimProfile
              providerUuid={providerUuid}
              claimed={data.claimed}
            />
            <AccreditationBadge data={data}/>
            <ActionButtons
              providerUuid={providerUuid}
              itemType="provider"
              data={data}
            />
            <WidgetBar data={data} />
            <TabsPanel
              data={data}
              itemType="provider"
              showReviews={true}
              showListings={true}
              selectedTab={tab}
              handleChange={handleTabChange}
            />
            <LocationPanel providerUuid={providerUuid} />
            <ProfileInfoPanel data={data} />
          </div>
        </Grid>
      </Grid>
    </Container>
  );
};

MobileLayout.propTypes = {
  providerUuid: PropTypes.string.isRequired,
  data: PropTypes.object.isRequired,
  subtitle: PropTypes.string.isRequired,
  tab: PropTypes.number.isRequired,
  handleTabChange: PropTypes.func.isRequired,
};

const commonStyles = makeStyles(() => ({
  root: {
    display: 'flex',
    flexWrap: 'wrap',
    alignContent: 'flex-start',
    minHeight: '65vh',
    padding: '2rem 1rem',
  },
  leftPane: {
    paddingRight: '3rem',
  }
}));

const ProfilePage = ({dispatch, match, data, isLoading, location}) => {
  const classes = commonStyles();
  const history = useHistory();
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down('sm'));
  const uuidOrSlug = match.params.uuid;
  const query = QueryString.parse(location.search);
  const initialTab = (('tab' in query) ? parseInt(query.tab) : 1) - 1; // 1 indexed is easier for normal people
  const [tab, setTab] = React.useState(initialTab);

  // Get the provider profile data
  const fetchProfile = async () => {
    dispatch(ProviderStore.isLoading('profile', true));
    try {
      const { payload } = await ProviderAPI.getProfile(uuidOrSlug);
      dispatch(ProviderStore.setProfile(payload));
      gtm.sendEvent('provider', {uuid: payload.uuid});
      track.sendEvent('provider profile', 'view', {
        slug: payload.slug,
      }, [payload.uuid]);
      history.replace('/provider/' + payload.slug + location.search);
    } catch (err) {
      if (err.status === 404) {
        history.replace('/404-page-not-found');
      }
      console.error(err); // eslint-disable-line no-console
    }
  };

  const handleTabChange = (event, newValue) => {
    setTab(newValue);
    track.sendEvent('provider profile tab', 'click', {value: newValue});
    history.push(`${location.pathname}?tab=${newValue+1}`);
  };

  // Flatten child services to concatenated string
  let subtitle = '';
  if ('services' in data) {
    const children = data['services'].map(d => d.children).flat();
    const stringList = children.map(e => e.name).join(', ');
    subtitle = `Providing ${stringList} services.`;
  }

  React.useEffect(() => {
    fetchProfile();
  }, [uuidOrSlug]); // eslint-disable-line

  // required for browser back button to work with tabs
  React.useEffect(() => {
    setTab(initialTab);
  }, [initialTab]); // eslint-disable-line

  if (isLoading) {
    return (
      <Page title={`${data.name} - Clickability`}>
        <LoadingSkeleton />
      </Page>
    );
  }

  return (
    <Page title={`${data.name} - Clickability`}>
      <MetaTags
        article
        title={data.name}
        description={`Read ratings and reviews from the people who use ${data.name}. Clickability is an Australian disability service directory.`}
        image={data.image_path}
        url={`${config.website.URL}/provider/${data.slug}`}
      />
      <div className={classes.root}>
        {mobile ? (
          <MobileLayout
            providerUuid={data.uuid}
            data={data}
            subtitle={subtitle}
            tab={tab}
            handleTabChange={handleTabChange}
          />
        ) : (
          <DesktopLayout
            providerUuid={data.uuid}
            data={data}
            subtitle={subtitle}
            tab={tab}
            handleTabChange={handleTabChange}
          />
        )}
      </div>
    </Page>
  );

};

ProfilePage.propTypes = {
  dispatch: PropTypes.func.isRequired,
  match: PropTypes.object.isRequired,
  data: PropTypes.object.isRequired,
  isLoading: PropTypes.bool.isRequired,
  location: PropTypes.object.isRequired,
};

const mapStateToProps = state => {
  return {
    data: state.provider.profile,
    isLoading: state.provider.profileLoading,
  };
};

export default connect(mapStateToProps)(ProfilePage);
